import { ReactNode, useLayoutEffect, useState, MouseEvent, isValidElement, cloneElement } from "react";
import { DialogContext, useDialogContext } from "../contexts/dialog.context";
import { createPortal } from "react-dom";


interface DialogProps {
    children: ReactNode;
    onClose?: () => void;
}

export const Dialog = ({ children, onClose }: DialogProps) => {
    const [isOpen, setIsOpen] = useState(false);

    const contextValue = {
        isOpen,
        setIsOpen,
        close: () => {
            setIsOpen(false);
            onClose?.();
        }
    }

    useLayoutEffect(() => {
        if (isOpen) {
            document.body.style.overflow = 'hidden';
        }
        return () => {
            document.body.style.overflow = 'unset';
        }
    }, [isOpen])

    return <DialogContext.Provider value={contextValue}>
        {children}
    </DialogContext.Provider>
}

interface DialogTriggerProps {
    children: ReactNode;
    disabled?: boolean;
}

Dialog.Trigger = ({ children, disabled }: DialogTriggerProps) => {
    const { setIsOpen } = useDialogContext();

    if (!isValidElement(children)) {
        throw new Error('Dialog.Trigger children must be a valid React element');
    }

    if (disabled) return children;

    return cloneElement(children, {
        onClick: (e: MouseEvent) => {
            children.props.onClick?.(e);
            setIsOpen(true);
        }
    })
}

Dialog.Close = ({ children }: { children?: ReactNode }) => {
    const { close } = useDialogContext();

    if (!children) {
        return <button onClick={close} className="relative w-[1em] h-[1em] after:content-[''] after:absolute after:-inset-1 after:rounded after:hover:bg-white/20">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="none" color="currentColor"><path d="M15.625 4.375L4.375 15.625M15.625 15.625L4.375 4.375" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round"></path></svg>
        </button>;
    }

    if (!isValidElement(children)) {
        throw new Error('Dialog.Close children must be a valid React element');
    }

    return cloneElement(children, {
        onClick: (e: MouseEvent) => {
            children.props.onClick?.(e);
            close();
        }
    })
}

Dialog.Content = ({ children }: { children: ReactNode }) => {
    const { isOpen, close } = useDialogContext();

    if (!isOpen) return null;

    const handleOverlayClick = (e: MouseEvent<HTMLDivElement>) => {
        if (e.target === e.currentTarget) {
            close();
        }
    };

    // The overlay owns the viewport; the panel is capped to it and scrolls its own overflow.
    // Without the cap, a panel taller than the screen (the checkout on a phone is one) overflowed
    // the centred flex line in *both* directions, so the header - and the only close button - sat
    // above the top of the screen with nothing to scroll it back into view.
    return createPortal(<div className="fixed inset-0 p-2 bg-black/80 flex items-center justify-center z-[1000]" onClick={handleOverlayClick} >
        <div className="bg-black rounded border border-white/20 max-h-full max-w-full overflow-y-auto overscroll-contain">
            {children}
        </div>
    </div>, document.body)

}